import { useContext } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Airplane, Check } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useAbsence } from "../../api/absence";
import { useCongSettings } from "../../api/cong";
import { hasAbsence } from "../../helpers/absence";
import { getOverlappingAssignmentLabel } from "../../helpers/conflictDetection";
import { getDayjs, ISODateFormat, stringToDate, weekOfString } from "../../helpers/dateHelpers";
import { HGContext } from "../../helpers/globals";
import { languageGroupName, myCongLanguageGroupName } from "../../helpers/langGroups";
import { userCompare } from "../../helpers/user";
import { getWeeklyAssignmentSummary as getWeekAssignmentsSummary } from "../../helpers/weeklyAssignmentSummary";
import { ISODateString } from "../../types/date";
import User from "../../types/user";
import { TagRendered } from "../congregation/tags";
import { NoOneWithPrivileges, OverlappingAssignment } from "./common";
import { AssignmentSummary, AssignmentSummaryType } from "./RecentAssignments";

export function UserAssignmentDropdowns(props: {
  users: User[];
  assignDate: ISODateString;
  langGroupId: number;
  assign: (userId: number) => void;
  currentAssignee?: number;
  assigneeFilter: string;
  privilegeAsset: string;
  hasAbsence?: (userId: number) => boolean;
  disableLangGroup?: boolean;
  disableAbsence?: boolean;
  assignmentMap?: Map<number, AssignmentSummary[]>;
  partType?: AssignmentSummaryType;
}) {
  const { t } = useTranslation();
  const absences = useAbsence();
  const myCong = useContext(HGContext).globals.cong!;
  const { data: congSettings } = useCongSettings(props.langGroupId);

  const langGroupIsCong = !props.langGroupId || props.langGroupId === myCong.id;
  const lgPossibleUsers = props.users.filter((u) =>
    langGroupIsCong ? !u.language_group_id : u.language_group_id === props.langGroupId,
  );
  const lgIds = new Set<number>(lgPossibleUsers.map((u) => u.id));
  const nonLgPossibleUsers = props.users.filter((u) => !lgIds.has(u.id));

  const showTwoUserLists = lgPossibleUsers.length > 0 && nonLgPossibleUsers.length > 0 && !props.disableLangGroup;

  const checkAbsence = (userId: number): boolean => {
    if (props.disableAbsence) return false;
    if (props.hasAbsence) return props.hasAbsence(userId);
    return !!absences.data && hasAbsence(userId, stringToDate(props.assignDate), absences.data);
  };

  const createAssignmentRings = (userId: number) => {
    if (!props.assignmentMap) return null;

    const userAssignments = props.assignmentMap.get(userId) || [];
    const currentWeek = weekOfString(props.assignDate);
    const rings = [];

    for (let i = -3; i <= 3; i++) {
      const weekDate = getDayjs(currentWeek).add(i, "week").format(ISODateFormat);
      const weekAssignments = userAssignments.filter((assignment) => weekOfString(assignment.date) === weekDate);

      const hasAssignment = weekAssignments.length > 0;
      const hasMatchingPart =
        props.partType && weekAssignments.some((assignment) => assignment.type === props.partType);
      const isCurrentWeek = i === 0;

      const weekAssignmentsSummary = getWeekAssignmentsSummary(weekDate, weekAssignments);

      rings.push(
        <OverlayTrigger
          key={i}
          placement="top"
          delay={{ show: 0, hide: 0 }}
          overlay={<Tooltip>{weekAssignmentsSummary}</Tooltip>}
        >
          <div className={`assignment-ring ${isCurrentWeek ? "current-week" : ""} ${hasAssignment ? "filled" : ""}`}>
            {hasMatchingPart && <Check />}
          </div>
        </OverlayTrigger>,
      );
    }

    return <div className="assignment-rings d-flex align-items-center">{rings}</div>;
  };

  const userDropdowns = (users: User[]) => {
    return users.sort(userCompare).map((u) => {
      const isAbsent = checkAbsence(u.id);
      const overlappingAssginmentLabel =
        props.assignmentMap && congSettings
          ? getOverlappingAssignmentLabel({
              userId: u.id,
              assignDate: props.assignDate,
              partType: props.partType,
              assignmentMap: props.assignmentMap,
              congSettings,
            })
          : null;
      const assignmentRings = createAssignmentRings(u.id);

      return (
        <div key={u.id}>
          <Dropdown.Item
            disabled={isAbsent}
            onClick={() => props.assign(u.id)}
            className="d-flex justify-content-between align-items-center"
          >
            <div className="d-flex align-items-center">
              <div className="conflict-icon-container">
                {!!overlappingAssginmentLabel && <OverlappingAssignment label={overlappingAssginmentLabel} />}
              </div>
              <div className="d-flex flex-column">
                <div>{u.displayName}</div>
                {u.tags && u.tags.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {u.tags.map((tag) => (
                      <TagRendered key={tag.id} tag={tag} badge mini />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center">
              {assignmentRings}
              {isAbsent && <Airplane className="ms-2" />}
            </div>
          </Dropdown.Item>
          <hr className="mx-3 my-1 border-secondary-subtle" />
        </div>
      );
    });
  };

  if (showTwoUserLists) {
    const secondHeader = props.langGroupId === myCong.id ? myCongLanguageGroupName() : myCong.name;
    return (
      <>
        <Dropdown.Header>{languageGroupName(props.langGroupId)}</Dropdown.Header>
        {userDropdowns(lgPossibleUsers)}
        <Dropdown.Divider />
        <Dropdown.Header>{secondHeader}</Dropdown.Header>
        {userDropdowns(nonLgPossibleUsers)}
      </>
    );
  }

  if (!props.users.length) {
    if (!props.currentAssignee && props.assigneeFilter === "" && props.privilegeAsset) {
      return <NoOneWithPrivileges key={0} priv={t(props.privilegeAsset)} />;
    }
    return null;
  }

  return (
    <>
      {lgPossibleUsers.length === 0 && (
        <Dropdown.Header>{langGroupIsCong ? myCongLanguageGroupName() : myCong.name}</Dropdown.Header>
      )}
      {userDropdowns(props.users)}
    </>
  );
}
